import { ref } from 'vue';
import { defineStore } from 'pinia';
import { FilterMatchMode } from '@primevue/core/api';

export type MeasurementRow = {
  inv_id: number;
  inv_number: string;
  inv_type: string | null;
  value: number;
  value_provider?: string | null;
};

export type SelectOption = {
  code: string;
  name: string;
  label?: string;
};

export type ProviderPriorityRow = {
  id: number;
  code: string;
  name: string;
  priority: number;
  isActive: boolean;
};

export type MeasurementTableFilters = Record<string, { value: string | string[] | null; matchMode: string }>;

// inv_number / value are user-filterable; value_provider is an equality (select) filter.
const FILTERABLE_FIELDS = ['inv_number', 'value'];

const buildDefaultFilters = (): MeasurementTableFilters =>
  Object.fromEntries([
    ...FILTERABLE_FIELDS.map(field => [field, { value: null, matchMode: FilterMatchMode.CONTAINS }]),
    ['value_provider', { value: null, matchMode: FilterMatchMode.EQUALS }],
    ['inv_type', { value: null, matchMode: FilterMatchMode.IN }],
  ]);

/**
 * Holds the Manage Measurements screen state so it survives the route component
 * being unmounted/remounted when the user navigates away and back. The component
 * fetches only when `initialized` is false, mirroring the ContactsHome pattern
 * (see useContactStore + useContacts.loadContactsHome).
 */
export const useMeasurementsStore = defineStore('measurements-store', () => {
  const measurements = ref<MeasurementRow[]>([]);
  const totalRecords = ref(0);

  // Profile selectors + their option lists.
  const providerOptions = ref<SelectOption[]>([]);
  const metricOptions = ref<SelectOption[]>([]);
  const segmentOptions = ref<SelectOption[]>([]);
  const intervalOptions = ref<SelectOption[]>([]);
  const venueOptions = ref<SelectOption[]>([]);
  const adminToolsEnabled = ref(false);

  const selectedProvider = ref<string>('default');
  const selectedMetric = ref<string>('impressions');
  const selectedSegment = ref<string>('P18+');
  const selectedInterval = ref<string>('weekly');

  // Table view state.
  const rowsPerPage = ref(100);
  const firstRecord = ref(0);
  const tableSortField = ref<string | null>(null);
  const tableSortOrder = ref<number | null>(null);
  const tableFilters = ref<MeasurementTableFilters>(buildDefaultFilters());

  // Admin provider-priority reorder state.
  const providerPriority = ref<ProviderPriorityRow[]>([]);
  const originalProviderOrder = ref<number[]>([]);

  // False until the first load completes; gates the on-mount fetch.
  const initialized = ref(false);

  return {
    measurements,
    totalRecords,
    providerOptions,
    metricOptions,
    segmentOptions,
    intervalOptions,
    venueOptions,
    adminToolsEnabled,
    selectedProvider,
    selectedMetric,
    selectedSegment,
    selectedInterval,
    rowsPerPage,
    firstRecord,
    tableSortField,
    tableSortOrder,
    tableFilters,
    providerPriority,
    originalProviderOrder,
    initialized,
  };
});
